"use client";
import Image from "next/image";
import { motion } from "framer-motion";
import { XCircle, CheckCircle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import RichText from "./RichText";

export default function WhyERPFail() {
  const { t, tv } = useLanguage();
  const fails = tv<string[]>("whyFail.fails");
  const benefits = tv<string[]>("whyFail.benefits");

  return (
    <section id="why-erp-fails" className="w-full scroll-mt-24 py-12 sm:py-16 sm:py-20 lg:py-24 bg-[#FAFCFF] overflow-hidden relative">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#EFF9F7] rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2 opacity-60 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#FDF2F2] rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2 opacity-50 pointer-events-none" />

      <div className="site-container relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <h2 className="font-heading section-heading text-[#0d1117] mb-4">
            {/* The accent here is the red italic serif run, not the usual
                brand-blue `section-heading-accent`. */}
            <RichText
              text={t("whyFail.heading")}
              accentClassName="text-red-500 italic font-serif"
            />
          </h2>
          <p className="text-base sm:text-lg text-[#596875] font-medium max-w-3xl mx-auto leading-relaxed">
            {t("whyFail.bodyLead")}
            <span className="text-red-600 font-bold"> {t("whyFail.bodyStrong")}</span> {t("whyFail.bodyTail")}
          </p>
        </motion.div>

        <div className="relative w-full mt-6 mb-12 flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-4 z-20">
          
          {/* THE FAILURE SIDE */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            whileHover={{ y: -6, scale: 1.015 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="w-full lg:w-[41%] p-5 sm:p-6 lg:p-7 rounded-[28px] lg:rounded-[32px] bg-white border border-red-100 shadow-[0_16px_35px_-10px_rgba(239,68,68,0.08)] hover:shadow-[0_25px_50px_-12px_rgba(239,68,68,0.2)] relative z-10 transition-shadow duration-300 cursor-pointer"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center text-white shadow-[0_0_15px_rgba(239,68,68,0.35)] shrink-0">
                <XCircle size={20} strokeWidth={2.5} />
              </div>
              <div>
                <h3 className="font-sora font-black text-xl text-[#0d1117]">{t("whyFail.failTitle")}</h3>
                <p className="text-[10px] font-bold text-red-500 uppercase tracking-wider">{t("whyFail.failSubtitle")}</p>
              </div>
            </div>

            <div className="flex flex-col gap-0 border border-red-50 rounded-2xl overflow-hidden bg-white/50">
              {fails.map((f, i) => (
                <div key={i} className="flex items-center gap-3 py-2.5 sm:py-3 px-4 border-b border-red-50 last:border-b-0 hover:bg-red-50/30 transition-colors">
                  <div className="w-4 h-4 rounded-full bg-red-400 flex items-center justify-center shrink-0">
                     <XCircle size={10} strokeWidth={2.5} className="text-white" />
                  </div>
                  <span className="text-[13px] text-[#1a202c] font-medium leading-tight">{f}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* ── CENTER PILLAR: FULLY VISIBLE SCHOOL ILLUSTRATION & VS BADGE ── */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-col items-center justify-center my-2 lg:my-0 lg:w-[18%] shrink-0 relative z-20"
          >
            {/* Soft Glow Aura behind the School */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#2A48C9]/15 via-brand-500/10 to-red-500/10 rounded-full blur-2xl pointer-events-none -z-10" />

            {/* Fully Visible 3D School Illustration */}
            <div className="relative flex items-center justify-center py-2">
              {/* next/image with intrinsic dimensions, replacing a raw <img>
                  that shipped the full 576KB PNG and declared no size — so it
                  was both the third-heaviest asset on the page and a
                  Lighthouse `unsized-images` hit. The largest box any
                  breakpoint gives it is 220px, which is what `sizes` caps the
                  generated variants at. width/height are the source's own
                  ratio; `w-[...] h-auto` still drives the rendered size. */}
              <Image
                src="/images/school-illustration-blue.png"
                alt={t("whyFail.schoolAlt")}
                width={1024}
                height={1024}
                sizes="220px"
                className="w-[180px] sm:w-[220px] lg:w-[200px] h-auto object-contain drop-shadow-xl hover:scale-105 transition-transform duration-300 pointer-events-none"
              />
              {/* VS Badge Overlaid over School Center */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30">
                <div className="w-13 h-13 sm:w-15 sm:h-15 lg:w-16 lg:h-16 rounded-full bg-white/95 shadow-2xl flex items-center justify-center font-sora font-black text-slate-800 text-base sm:text-lg lg:text-xl border-4 border-[#2A48C9]/40 backdrop-blur-md ring-4 ring-white">
                  VS
                </div>
              </div>
            </div>
          </motion.div>

          {/* THE SUCCESS SIDE */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            whileHover={{ y: -6, scale: 1.015 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="w-full lg:w-[41%] p-5 sm:p-6 lg:p-7 rounded-[28px] lg:rounded-[32px] bg-[#EFF3FE] border border-[#687FE7]/30 shadow-[0_16px_35px_-10px_rgba(43,179,164,0.12)] hover:shadow-[0_25px_50px_-12px_rgba(43,179,164,0.22)] relative z-10 transition-shadow duration-300 cursor-pointer"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-[#2A48C9] flex items-center justify-center text-white shadow-[0_0_15px_rgba(61,94,225,0.35)] shrink-0">
                <CheckCircle size={20} strokeWidth={2.5} />
              </div>
              <div>
                <h3 className="font-sora font-black text-xl text-[#0d1117]">{t("whyFail.winTitle")}</h3>
                <p className="text-[10px] font-bold text-[#3D5EE1] uppercase tracking-wider">{t("whyFail.winSubtitle")}</p>
              </div>
            </div>

            <div className="flex flex-col gap-0 border border-[#687FE7]/20 rounded-2xl overflow-hidden bg-white/40">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-center gap-3 py-2.5 sm:py-3 px-4 border-b border-[#687FE7]/20 last:border-b-0 hover:bg-white/60 transition-colors">
                  <div className="w-4 h-4 rounded-full bg-[#687FE7] flex items-center justify-center shrink-0">
                     <CheckCircle size={10} strokeWidth={2.5} className="text-white" />
                  </div>
                  <span className="text-[13px] text-[#1a202c] font-medium leading-tight">{b}</span>
                </div>
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

